import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  fetchCrmJobs, jobAction, createCrmJob, dispatchJob, jobInvoice,
  invoicePaymentLink, invoiceMarkPaid, sendInvoice, fetchPricebook, money2,
  getJobDiagnosis, saveJobDiagnosis,
} from "@/lib/dashboardApi";
import {
  Loader2, Navigation, MapPin, CheckCircle2, Play, Pause, Clock, Plus, Timer,
  FileText, Send, Lock, CreditCard, ReceiptText, BadgeCheck, Home, Building2, Mail,
  Eye, ChevronUp, ChevronDown, Save, Stethoscope, Snowflake, Lightbulb, History, X, Phone as PhoneIcon,
} from "lucide-react";

const PROGRAMS = [
  { key: "retail", name: "AQUILA RETAIL RESIDENTIAL", short: "Retail Residential", icon: Home, cls: "bg-emerald-600" },
  { key: "jays", name: "AQUILA PROPERTY MANAGEMENT", short: "Property Mgmt (Jay's)", icon: Building2, cls: "bg-indigo-600" },
];
const prog = (k) => PROGRAMS.find((p) => p.key === k);

const fmt = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${ss}`;
};
const STATUS_STYLE = {
  Traveling: "bg-amber-100 text-amber-700",
  "On Site": "bg-emerald-100 text-emerald-700",
  Dispatched: "bg-sky-100 text-sky-700",
  Completed: "bg-slate-200 text-slate-600",
};

export default function TechJobs() {
  const [jobs, setJobs] = useState([]);
  const [retailItems, setRetailItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [nj, setNj] = useState({ title: "", program: "", customer_name: "", customer_email: "", customer_phone: "" });

  const load = async () => {
    try {
      const [j, pb] = await Promise.all([fetchCrmJobs(), fetchPricebook("retail").catch(() => ({ items: [] }))]);
      setJobs(j);
      setRetailItems((pb.items || []).filter((i) => i.book === "retail" && i.price != null && i.active !== false));
    } catch (e) { console.error(e); toast.error("Could not load jobs."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = (id, u) => setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...u } : j)));
  const act = async (id, action) => {
    try { patch(id, await jobAction(id, action)); }
    catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const addJob = async () => {
    if (!nj.title.trim()) return toast.error("Job title required");
    if (!nj.program) return toast.error("Select a pricing program");
    try {
      await createCrmJob(nj);
      toast.success("Job created");
      setNj({ title: "", program: "", customer_name: "", customer_email: "", customer_phone: "" });
      setCreating(false); load();
    } catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Create failed"); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;
  const active = jobs.filter((j) => j.status !== "Completed");
  const done = jobs.filter((j) => j.status === "Completed");

  return (
    <div className="max-w-4xl" data-testid="techjobs-panel">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-navy">My Jobs</h1>
          <p className="text-sm text-slate-500 mt-1">Every job carries a locked pricing program. Traveling → Arrived (starts the clock) → Complete → Invoice → Payment.</p>
        </div>
        <button onClick={() => setCreating((c) => !c)} data-testid="techjob-new-toggle" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-launch shrink-0"><Plus className="h-4 w-4" /> New job</button>
      </div>

      {creating && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 space-y-3" data-testid="techjob-new-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <input placeholder="Job title *" value={nj.title} onChange={(e) => setNj({ ...nj, title: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="techjob-new-title" />
            <select value={nj.program} onChange={(e) => setNj({ ...nj, program: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="techjob-new-program">
              <option value="">Select pricing program *</option>
              {PROGRAMS.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
            <input placeholder="Customer name" value={nj.customer_name} onChange={(e) => setNj({ ...nj, customer_name: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="techjob-new-customer" />
            <input placeholder="Customer email (optional)" value={nj.customer_email} onChange={(e) => setNj({ ...nj, customer_email: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="techjob-new-email" />
            <input placeholder="Customer phone (optional)" value={nj.customer_phone} onChange={(e) => setNj({ ...nj, customer_phone: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" />
          </div>
          <div className="flex gap-2">
            <button onClick={addJob} className="inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid="techjob-new-save"><Plus className="h-4 w-4" /> Create job</button>
            <button onClick={() => setCreating(false)} className="text-sm text-slate-500 px-3">Cancel</button>
          </div>
          <p className="text-xs text-slate-400">A pricing program is required and locks once the job is dispatched.</p>
        </div>
      )}

      <div className="space-y-4">
        {active.length === 0 && <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-6">No active jobs. Create one above.</p>}
        {active.map((j) => <JobCard key={j.id} job={j} act={act} patch={patch} retailItems={retailItems} />)}
      </div>
      {done.length > 0 && (
        <div className="mt-8">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Completed today</p>
          <div className="space-y-4">{done.map((j) => <JobCard key={j.id} job={j} act={act} patch={patch} retailItems={retailItems} completed />)}</div>
        </div>
      )}
    </div>
  );
}

function ProgramBadge({ job }) {
  const p = prog(job.program);
  if (!p) return <span className="text-[11px] font-bold uppercase px-2 py-1 rounded-full bg-red-100 text-red-600" data-testid={`techjob-program-${job.id}`}>No program set</span>;
  const Icon = p.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase px-2.5 py-1 rounded-full text-white ${p.cls}`} data-testid={`techjob-program-${job.id}`}>
      <Icon className="h-3.5 w-3.5" /> {p.name}{job.program_locked && <Lock className="h-3 w-3 ml-0.5" />}
    </span>
  );
}

function JobCard({ job, act, patch, retailItems, completed }) {
  const [display, setDisplay] = useState(job.billable_seconds || 0);
  const baseRef = useRef({ base: job.billable_seconds || 0, at: Date.now() });

  useEffect(() => { baseRef.current = { base: job.billable_seconds || 0, at: Date.now() }; setDisplay(job.billable_seconds || 0); }, [job.billable_seconds, job.timer_running]);
  useEffect(() => {
    if (!job.timer_running) return;
    const t = setInterval(() => { const { base, at } = baseRef.current; setDisplay(base + (Date.now() - at) / 1000); }, 1000);
    return () => clearInterval(t);
  }, [job.timer_running]);

  const [open, setOpen] = useState(false);
  const status = job.status || "Scheduled";
  const canTravel = !["Traveling", "On Site", "Completed"].includes(status);

  const dispatch = async () => {
    try { patch(job.id, await dispatchJob(job.id)); toast.success("Job dispatched — program locked"); }
    catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Dispatch failed"); }
  };

  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-4 ${completed ? "opacity-90" : ""}`} data-testid={`techjob-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-bold text-navy truncate">{job.title}</p>
          <p className="text-xs text-slate-400 truncate">{job.customer_name || "—"}</p>
        </div>
        <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded-full ${STATUS_STYLE[status] || "bg-slate-100 text-slate-500"}`} data-testid={`techjob-status-${job.id}`}>{status}</span>
      </div>

      {/* Prominent locked pricing program */}
      <div className="mt-3"><ProgramBadge job={job} /></div>

      {/* Billable timer */}
      <div className="mt-3 flex items-center gap-2">
        <Clock className={`h-4 w-4 ${job.timer_running ? "text-emerald-500" : "text-slate-400"}`} />
        <span className="font-mono text-lg font-bold text-navy tabular-nums" data-testid={`techjob-timer-${job.id}`}>{fmt(display)}</span>
        <span className="text-xs text-slate-400">billable</span>
        {job.timer_running && <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> running</span>}
      </div>

      {!completed && (
        <div className="mt-4 flex flex-wrap gap-2">
          {!job.dispatched && (
            <button onClick={dispatch} data-testid={`techjob-dispatch-${job.id}`} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold text-sm px-4 py-2.5 rounded-lg"><Send className="h-4 w-4" /> Dispatch</button>
          )}
          {canTravel && <button onClick={() => act(job.id, "travel")} data-testid={`techjob-travel-${job.id}`} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg"><Navigation className="h-4 w-4" /> Traveling</button>}
          {status === "Traveling" && <button onClick={() => act(job.id, "arrive")} data-testid={`techjob-arrive-${job.id}`} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2.5 rounded-lg"><MapPin className="h-4 w-4" /> Arrived</button>}
          {status === "On Site" && (job.timer_running
            ? <button onClick={() => act(job.id, "stop")} data-testid={`techjob-timer-toggle-${job.id}`} className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold text-sm px-4 py-2.5 rounded-lg"><Pause className="h-4 w-4" /> Pause timer</button>
            : <button onClick={() => act(job.id, "start")} data-testid={`techjob-timer-toggle-${job.id}`} className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold text-sm px-4 py-2.5 rounded-lg"><Play className="h-4 w-4" /> Resume timer</button>)}
          {(status === "On Site" || status === "Traveling") && <button onClick={() => act(job.id, "complete")} data-testid={`techjob-complete-${job.id}`} className="inline-flex items-center gap-2 bg-navy hover:bg-navy/90 text-white font-bold text-sm px-4 py-2.5 rounded-lg"><CheckCircle2 className="h-4 w-4" /> Complete</button>}
        </div>
      )}

      {completed && (
        <p className="mt-3 text-sm text-slate-600 flex items-center gap-2"><Timer className="h-4 w-4 text-slate-400" /> Billable: <b className="text-navy">{fmt(job.billable_seconds)}</b> ({job.billable_minutes} min)</p>
      )}

      <button onClick={() => setOpen((o) => !o)} data-testid={`techjob-viewjob-${job.id}`} className="mt-3 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-4 py-2 rounded-lg">
        <Eye className="h-4 w-4" /> {open ? "Hide job" : "View job"} {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && <JobDetail job={job} patch={patch} retailItems={retailItems} />}
    </div>
  );
}

const MEAS = [
  ["outdoor_temp", "Outdoor Temp (°F)"], ["indoor_temp", "Indoor Temp (°F)"], ["return_air", "Return Air (°F)"],
  ["supply_air", "Supply Air (°F)"], ["temp_split", "Temp Split (°F)"], ["indoor_rh", "Indoor RH (%)"],
  ["line_voltage", "Line Voltage (V)"], ["compressor_amps", "Compressor Amps"], ["cond_fan_amps", "Cond. Fan Amps"],
  ["blower_amps", "Blower Amps"], ["cap_rated", "Capacitor Rated (µF)"], ["cap_actual", "Capacitor Actual (µF)"],
  ["suction_psi", "Suction Pressure (psi)"], ["liquid_psi", "Liquid Pressure (psi)"], ["superheat", "Superheat (°F)"],
  ["subcooling", "Subcooling (°F)"], ["return_static", "Return Static (in wc)"], ["supply_static", "Supply Static (in wc)"],
];
const SUMMARY = [
  ["problem_found", "Problem found"], ["root_cause", "Root cause"], ["recommended_repair", "Recommended repair"],
  ["system_condition", "Overall system condition"], ["safety_concern", "Safety concern (if any)"],
  ["additional_concerns", "Additional concerns"], ["tech_recommendation", "Technician recommendation"],
];
const REFRIG_TYPES = ["R-410A", "R-22", "R-32", "R-454B", "R-407C", "R-421A"];
const DETAIL_TABS = [["overview", "Overview"], ["diagnosis", "Diagnosis"], ["invoice", "Invoice"], ["history", "History"]];

function JobDetail({ job, patch, retailItems }) {
  const [tab, setTab] = useState("overview");
  const [d, setD] = useState({ measurements: {}, summary: {}, refrigerant: { type: "R-410A", added: "", recovered: "", leak: false }, recommendations: [] });
  const [rec, setRec] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getJobDiagnosis(job.id).then((x) => {
      if (x && Object.keys(x).length) {
        setD((p) => ({
          measurements: x.measurements || {}, summary: x.summary || {},
          refrigerant: (Array.isArray(x.refrigerant) && x.refrigerant[0]) || p.refrigerant,
          recommendations: x.recommendations || [],
        }));
      }
    }).catch(() => {});
  }, [job.id]);

  const setMeas = (k, v) => setD((p) => ({ ...p, measurements: { ...p.measurements, [k]: v } }));
  const setSum = (k, v) => setD((p) => ({ ...p, summary: { ...p.summary, [k]: v } }));
  const setRef = (k, v) => setD((p) => ({ ...p, refrigerant: { ...p.refrigerant, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await saveJobDiagnosis(job.id, { measurements: d.measurements, summary: d.summary, refrigerant: [d.refrigerant], recommendations: d.recommendations });
      toast.success("Diagnosis saved");
    } catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const info = (label, value) => (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value || "—"}</p>
    </div>
  );

  return (
    <div className="mt-4 border-t border-slate-200 pt-4" data-testid={`techjob-detail-${job.id}`}>
      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
        {DETAIL_TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`jobdetail-tab-${k}-${job.id}`}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === k ? "border-launch text-launch" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid={`jobdetail-overview-${job.id}`}>
          {info("Job type", job.job_type || "Service Call")}
          {info("Source", job.source)}
          {info("Priority", job.priority || "Standard")}
          {info("Customer", job.customer_name)}
          {info("Phone", job.customer_phone)}
          {info("Email", job.customer_email)}
          {info("Assigned tech", job.status_by || job.assigned_tech)}
          {info("Status", job.status)}
          <div className="col-span-2 sm:col-span-3">{info("Complaint", job.title || job.complaint)}</div>
        </div>
      )}

      {tab === "diagnosis" && (
        <div className="space-y-6" data-testid={`jobdetail-diagnosis-${job.id}`}>
          <div>
            <p className="flex items-center gap-2 font-heading font-bold text-navy mb-3"><Stethoscope className="h-4 w-4 text-launch" /> Measurements</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {MEAS.map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-[11px] text-slate-500">{label}</span>
                  <input value={d.measurements[k] || ""} onChange={(e) => setMeas(k, e.target.value)} data-testid={`meas-${k}-${job.id}`}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5" />
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="font-heading font-bold text-navy mb-3">Diagnostic Summary</p>
            <div className="space-y-3">
              {SUMMARY.map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-[11px] text-slate-500">{label}</span>
                  <textarea rows={2} value={d.summary[k] || ""} onChange={(e) => setSum(k, e.target.value)} data-testid={`sum-${k}-${job.id}`}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5 resize-y" />
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="flex items-center gap-2 font-heading font-bold text-navy mb-3"><Snowflake className="h-4 w-4 text-sky-500" /> Refrigerant Log</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
              <label className="block">
                <span className="text-[11px] text-slate-500">Type</span>
                <select value={d.refrigerant.type} onChange={(e) => setRef("type", e.target.value)} data-testid={`ref-type-${job.id}`} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5">
                  {REFRIG_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label className="block"><span className="text-[11px] text-slate-500">Added (lb)</span>
                <input value={d.refrigerant.added || ""} onChange={(e) => setRef("added", e.target.value)} data-testid={`ref-added-${job.id}`} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5" /></label>
              <label className="block"><span className="text-[11px] text-slate-500">Recovered (lb)</span>
                <input value={d.refrigerant.recovered || ""} onChange={(e) => setRef("recovered", e.target.value)} data-testid={`ref-recovered-${job.id}`} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5" /></label>
              <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
                <input type="checkbox" checked={!!d.refrigerant.leak} onChange={(e) => setRef("leak", e.target.checked)} data-testid={`ref-leak-${job.id}`} /> Leak found
              </label>
            </div>
          </div>

          <div>
            <p className="flex items-center gap-2 font-heading font-bold text-navy mb-3"><Lightbulb className="h-4 w-4 text-amber-500" /> Recommendations</p>
            <div className="space-y-2">
              {d.recommendations.map((r, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <span className="flex-1">{r}</span>
                  <button onClick={() => setD((p) => ({ ...p, recommendations: p.recommendations.filter((_, x) => x !== i) }))} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={rec} onChange={(e) => setRec(e.target.value)} placeholder="e.g. Surge protector" data-testid={`rec-input-${job.id}`} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <button onClick={() => { if (rec.trim()) { setD((p) => ({ ...p, recommendations: [...p.recommendations, rec.trim()] })); setRec(""); } }} data-testid={`rec-add-${job.id}`} className="inline-flex items-center gap-1 bg-slate-900 text-white text-sm font-bold px-3 py-2 rounded-lg"><Plus className="h-4 w-4" /> Add</button>
              </div>
            </div>
          </div>

          <button onClick={save} disabled={saving} data-testid={`jobdetail-save-${job.id}`} className="inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold text-sm px-5 py-2.5 rounded-lg disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Diagnosis
          </button>
        </div>
      )}

      {tab === "invoice" && (
        <div data-testid={`jobdetail-invoice-${job.id}`}>
          <JobInvoice job={job} patch={patch} retailItems={retailItems} />
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-2 text-sm" data-testid={`jobdetail-history-${job.id}`}>
          {[["Traveling", job.travel_started_at], ["Arrived on site", job.arrived_at], ["Completed", job.completed_at]]
            .filter(([, t]) => t).map(([label, t]) => (
              <div key={label} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <History className="h-4 w-4 text-slate-400" />
                <span className="font-semibold text-slate-700">{label}</span>
                <span className="text-slate-500 ml-auto">{new Date(t).toLocaleString()}</span>
              </div>
            ))}
          {!job.travel_started_at && !job.arrived_at && !job.completed_at && <p className="text-slate-500">No status history yet.</p>}
          {job.status_by && <p className="text-xs text-slate-400 mt-1">Last updated by {job.status_by}</p>}
        </div>
      )}
    </div>
  );
}

function JobInvoice({ job, patch, retailItems }) {
  const [inv, setInv] = useState(job.invoice_number ? { id: job.invoice_id, number: job.invoice_number, amount: job.invoice_amount, status: job.invoice_status } : null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState([]);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const createJays = async () => {
    setBusy(true);
    try { const c = await jobInvoice(job.id); setInv(c); toast.success(c.already_existed ? `Invoice ${c.number} exists` : `Invoice ${c.number} created`); }
    catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Could not create invoice"); }
    finally { setBusy(false); }
  };
  const createRetail = async () => {
    if (!picked.length) return toast.error("Select at least one repair");
    setBusy(true);
    try { const c = await jobInvoice(job.id, { item_ids: picked }); setInv(c); setPicking(false); toast.success(`Invoice ${c.number} created`); }
    catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Could not create invoice"); }
    finally { setBusy(false); }
  };
  const markPaid = async (method) => {
    setBusy(true);
    try { const p = await invoiceMarkPaid(inv.id, method); setInv({ ...inv, status: "Paid", receipt_number: p.receipt_number }); if (job.id) patch(job.id, { invoice_status: "Paid" }); toast.success(`Paid · ${p.receipt_number}`); }
    catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Could not record payment"); }
    finally { setBusy(false); }
  };
  const paymentLink = async () => {
    setBusy(true);
    try { const r = await invoicePaymentLink(inv.id); window.open(r.url, "_blank"); toast.success("Square checkout opened"); }
    catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Payment link unavailable"); }
    finally { setBusy(false); }
  };
  const emailInvoice = async () => {
    setBusy(true);
    try {
      const r = await sendInvoice(inv.id);
      if (job.id) patch(job.id, { invoice_status: "Sent" });
      setInv((v) => ({ ...v, status: v.status === "Draft" ? "Sent" : v.status }));
      toast.success(r.pay_url ? `Emailed to ${r.emailed_to} with pay link` : `Invoice emailed to ${r.emailed_to}`);
    } catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Could not send invoice"); }
    finally { setBusy(false); }
  };

  if (!job.program) return <p className="text-xs text-red-500" data-testid={`techjob-invoice-hint-${job.id}`}>This job has no pricing program — a dispatcher must set one before invoicing.</p>;

  if (!inv) {
    if (job.program === "jays")
      return <button onClick={createJays} disabled={busy} data-testid={`techjob-invoice-${job.id}`} className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-4 py-2.5 rounded-lg disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Create invoice (Jay's labor)</button>;
    // retail: pick flat-rate repairs
    return (
      <div data-testid={`techjob-invoice-retail-${job.id}`}>
        {!picking ? (
          <button onClick={() => setPicking(true)} data-testid={`techjob-invoice-${job.id}`} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2.5 rounded-lg"><FileText className="h-4 w-4" /> Create invoice (select repairs)</button>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-slate-600 mb-2">Select flat-rate repairs (Comfort Club discount auto-applies for members):</p>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {retailItems.map((it) => (
                <label key={it.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={picked.includes(it.id)} onChange={() => setPicked((p) => p.includes(it.id) ? p.filter((x) => x !== it.id) : [...p, it.id])} data-testid={`techjob-repair-${it.id}`} />
                  {it.name} — {money2(it.price)}{it.cc_exempt && <span className="text-[10px] text-amber-600 uppercase">no CC</span>}
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={createRetail} disabled={busy} className="inline-flex items-center gap-2 bg-emerald-600 text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid={`techjob-invoice-save-${job.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Create ({picked.length})</button>
              <button onClick={() => setPicking(false)} className="text-sm text-slate-500 px-3">Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const paid = inv.status === "Paid";
  return (
    <div className="space-y-2" data-testid={`techjob-invoice-done-${job.id}`}>
      <div className={`inline-flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${paid ? "text-emerald-700 bg-emerald-50 border border-emerald-200" : "text-slate-700 bg-slate-50 border border-slate-200"}`}>
        {paid ? <BadgeCheck className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
        Invoice <b>{inv.number}</b>{inv.amount != null && <> · {money2(inv.amount)}</>} · <span className="uppercase text-xs font-bold">{paid ? "Paid" : (inv.status || "Draft")}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {!paid && <button onClick={emailInvoice} disabled={busy} data-testid={`techjob-send-${job.id}`} className="inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white text-xs font-bold px-3 py-2 rounded-lg disabled:opacity-60"><Mail className="h-4 w-4" /> Email invoice to customer</button>}
        {!paid && <button onClick={paymentLink} disabled={busy} data-testid={`techjob-paylink-${job.id}`} className="inline-flex items-center gap-2 bg-navy text-white text-xs font-bold px-3 py-2 rounded-lg disabled:opacity-60"><CreditCard className="h-4 w-4" /> Square payment link</button>}
        {!paid && <button onClick={() => markPaid("cash")} disabled={busy} data-testid={`techjob-markpaid-${job.id}`} className="inline-flex items-center gap-2 bg-emerald-600 text-white text-xs font-bold px-3 py-2 rounded-lg disabled:opacity-60"><CheckCircle2 className="h-4 w-4" /> Mark paid (cash/check/terminal)</button>}
        <a href={`${backend}/api/crm/invoices/${inv.id}/receipt`} target="_blank" rel="noreferrer" data-testid={`techjob-receipt-${job.id}`} className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg"><ReceiptText className="h-4 w-4" /> View receipt</a>
      </div>
    </div>
  );
}
